import React, { useState, useEffect } from 'react';
import trainerService from '../api/trainerService';
import centerService from '../api/centerService';
import authService from '../api/authService';
import './ManagementPages.css'; // Your existing styles for this page
import '../components/Modal.css'; // Re-use the modal styles

const UserManagement = () => {
  const [trainers, setTrainers] = useState([]);
  const [centers, setCenters] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTrainer, setEditingTrainer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fieldErrors, setFieldErrors] = useState({});

  const initialFormState = {
    name: '', email: '', password: '', phone: '', role: 'trainer',
    specialization: '', experience_years: '', centerId: ''
  };
  const [formData, setFormData] = useState(initialFormState);

  // --- Permissions ---
  const currentUser = authService.getCurrentUser();
  const isScheduler = currentUser && currentUser.role === 'scheduler';

  // --- Data Fetching ---
  useEffect(() => {
    setLoading(true);
    Promise.allSettled([
      trainerService.getAllTrainers(),
      centerService.getAllCenters()
    ]).then(([trainersRes, centersRes]) => {
      if (trainersRes.status === 'fulfilled') {
        setTrainers(trainersRes.value.data);
      }
      if (centersRes.status === 'fulfilled') {
        setCenters(centersRes.value.data);
      }
    }).finally(() => setLoading(false));
  }, []);

  // --- Modal and Form Handlers ---
  const handleEdit = (trainer) => {
    setEditingTrainer(trainer);
    setFormData({
      name: trainer.name || '',
      email: trainer.email || '',
      password: '', // Leave blank for editing - only update if filled
      phone: trainer.phone || '',
      role: trainer.role || 'trainer',
      specialization: trainer.specialization || '',
      experience_years: trainer.experience_years || '',
      centerId: trainer.centerId || ''
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTrainer(null);
    setFormData(initialFormState);
    setFieldErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Parse error response and extract field-level errors
  const parseErrors = (error) => {
    const errors = {};
    const response = error.response?.data;

    if (response?.errors && Array.isArray(response.errors)) {
      response.errors.forEach(err => {
        const field = err.path || err.param || err.field;
        if (field) {
          errors[field] = err.msg || err.message;
        }
      });
    } else if (response?.message) {
      const msg = response.message.toLowerCase();
      if (msg.includes('name')) {
        errors.name = response.message;
      } else if (msg.includes('email')) {
        errors.email = response.message;
      } else if (msg.includes('password')) {
        errors.password = response.message;
      } else if (msg.includes('phone')) {
        errors.phone = response.message;
      } else if (msg.includes('role')) {
        errors.role = response.message;
      } else {
        errors._general = response.message;
      }
    } else {
      errors._general = 'Could not save facilitator. Please check your input.';
    }
    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFieldErrors({});

    // Prepare data to send - exclude password if empty (for updates)
    const dataToSend = { ...formData };
    if (editingTrainer && !formData.password) {
      delete dataToSend.password; // Don't send password if editing and field is empty
    }

    const action = editingTrainer
      ? trainerService.updateTrainer(editingTrainer.id, dataToSend)
      : trainerService.createTrainer(dataToSend);

    action.then(() => {
      trainerService.getAllTrainers().then(res => setTrainers(res.data));
      handleCloseModal();
    }).catch(err => {
      const errors = parseErrors(err);
      setFieldErrors(errors);
    });
  };
  
  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this trainer?')) {
      trainerService.deleteTrainer(id).then(() => {
        trainerService.getAllTrainers().then(res => setTrainers(res.data));
        alert('Trainer deleted successfully!');
      }).catch(err => {
        alert(`Error deleting trainer: ${err.response?.data?.message || err.message}`);
      });
    }
  };

  const filteredTrainers = trainers.filter(trainer =>
    (trainer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
     trainer.email.toLowerCase().includes(searchTerm.toLowerCase())) &&
    (filterRole === '' || trainer.role === filterRole)
  );

  // Calculate stats for compact display
  const totalTrainers = trainers.length;
  const activeTrainers = trainers.filter(t => t.role === 'trainer').length;

  return (
    <div className="management-page">
      {/* Compact Header with inline stats */}
      <div className="page-header-compact">
        <div className="header-left">
          <h1 className="page-title">
            <span className="page-title-icon">👩‍🏫</span> Facilitator Management
          </h1>
          <div className="inline-stats">
            <span className="stat-item">📊 {totalTrainers} Facilitators</span>
            <span className="stat-item">🟢 {activeTrainers} Active</span>
          </div>
        </div>
        {isScheduler && (
          <button className="add-btn-primary" onClick={() => setShowModal(true)}>
            + Add Facilitator
          </button>
        )}
      </div>

      {/* Search and Filter Controls */}
      <div className="filter-bar fade-in">
        <div className="search-field">
          <input
            type="text"
            placeholder="🔍 Search facilitators..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-field">
          <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
            <option value="">All Roles</option>
            <option value="trainer">Facilitator</option>
            <option value="scheduler">Scheduler</option>
            <option value="admin">Admin</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading-spinner fade-in">
          <div className="spinner"></div>
          <span>Loading facilitator data...</span>
        </div>
      ) : (
        <table className="management-table fade-in">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Role</th>
              <th>Specialization</th>
              <th>Experience</th>
              <th>Center</th>
              {isScheduler && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filteredTrainers.map(trainer => (
              <tr key={trainer.id}>
                <td>
                  <strong>{trainer.name}</strong>
                </td>
                <td>{trainer.email}</td>
                <td>{trainer.phone}</td>
                <td>
                  <span className={`role-badge ${trainer.role}`}>{trainer.role}</span>
                </td>
                <td>{trainer.specialization || 'N/A'}</td>
                <td>
                  <span className="experience-badge">{trainer.experience_years}y</span>
                </td>
                <td>{trainer.center?.name || 'N/A'}</td>
                {isScheduler && (
                  <td className="actions-cell">
                    <button
                      onClick={() => handleEdit(trainer)}
                      className="action-button edit"
                      title="Edit facilitator"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDelete(trainer.id)}
                      className="action-button delete"
                      title="Delete facilitator"
                    >
                      🗑️ Delete
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Enhanced Modal for Add/Edit Facilitator */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content enhanced" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingTrainer ? '✏️ Edit Facilitator' : '➕ Add New Facilitator'}</h3>
              <button 
                className="modal-close"
                onClick={() => setShowModal(false)}
              >
                ✖
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              {fieldErrors._general && (
                <div className="form-error-banner">
                  ⚠️ {fieldErrors._general}
                </div>
              )}
              <div className="form-grid">
                <div className={`form-field ${fieldErrors.name ? 'has-error' : ''}`}>
                  <label>👤 Full Name</label>
                  <input
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter full name"
                    required
                    className={fieldErrors.name ? 'input-error' : ''}
                  />
                  {fieldErrors.name && <span className="field-error">{fieldErrors.name}</span>}
                </div>

                <div className={`form-field ${fieldErrors.email ? 'has-error' : ''}`}>
                  <label>📧 Email</label>
                  <input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter email address"
                    required
                    className={fieldErrors.email ? 'input-error' : ''}
                  />
                  {fieldErrors.email && <span className="field-error">{fieldErrors.email}</span>}
                </div>

                <div className={`form-field ${fieldErrors.password ? 'has-error' : ''}`}>
                  <label>🔒 Password {editingTrainer && <span style={{fontSize: '0.8em', color: '#666'}}>(leave blank to keep current)</span>}</label>
                  <input
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder={editingTrainer ? "Enter new password (optional)" : "Enter password"}
                    required={!editingTrainer}
                    minLength="8"
                    className={fieldErrors.password ? 'input-error' : ''}
                  />
                  {fieldErrors.password && <span className="field-error">{fieldErrors.password}</span>}
                </div>

                <div className={`form-field ${fieldErrors.phone ? 'has-error' : ''}`}>
                  <label>📞 Phone</label>
                  <input
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter phone number"
                    required
                    className={fieldErrors.phone ? 'input-error' : ''}
                  />
                  {fieldErrors.phone && <span className="field-error">{fieldErrors.phone}</span>}
                </div>

                <div className={`form-field ${fieldErrors.role ? 'has-error' : ''}`}>
                  <label>💼 Role</label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    required
                    className={fieldErrors.role ? 'input-error' : ''}
                  >
                    <option value="trainer">Facilitator</option>
                    <option value="scheduler">Scheduler</option>
                    <option value="admin">Admin</option>
                  </select>
                  {fieldErrors.role && <span className="field-error">{fieldErrors.role}</span>}
                </div>
                
                <div className="form-field">
                  <label>🎖️ Specialization</label>
                  <input 
                    name="specialization" 
                    value={formData.specialization} 
                    onChange={handleInputChange} 
                    placeholder="Area of expertise" 
                  />
                </div>
                
                <div className="form-field">
                  <label>⏰ Experience (Years)</label>
                  <input 
                    name="experience_years" 
                    type="number" 
                    value={formData.experience_years} 
                    onChange={handleInputChange} 
                    placeholder="Years of experience" 
                  />
                </div>
                
                <div className="form-field full-width">
                  <label>🏢 Center</label>
                  <select name="centerId" value={formData.centerId} onChange={handleInputChange}>
                    <option value="">-- Select Center --</option>
                    {centers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
              
              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={handleCloseModal}>
                  ❌ Cancel
                </button>
                <button type="submit" className="submit-btn">
                  {editingTrainer ? '💾 Update Facilitator' : '➕ Create Facilitator'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;